import React, { FC, useEffect, useState } from "react";

import { PieChart, Pie, Cell } from "recharts";
import { observer } from "mobx-react";

import PieChartIcon from "../../../../../../images/activity-dashboard/pie-chart-slate-blue.svg";

import { DashboardService } from "../../../shared/services";

import {
  AuthService,
  CustomLabel,
  FlexContainer,
  Loader,
  ToastService,
} from "@mindmemobile/mm-shared-global";

import { PaymentBreakDownModel } from "../../../shared/models";
import { ColorBox } from "../../../shared/components";

type IProps = {
  merchantAccountId: string;
};

const ONE_TIME_COLOR = "#FF9E80";
const RECURRING_COLOR = "#556BB8";
const BREAKDOWN_WINDOW_DAYS = 90;

const PaymentBreakDownWidget: FC<IProps> = ({ merchantAccountId }) => {
  const [showLoader, setLoader] = useState(false);
  const [oneTime, setOneTime] = useState(0);
  const [recurring, setRecurring] = useState(0);
  const [oneTimePercentage, setOneTimePercentage] = useState(0);
  const [recurringPercentage, setRecurringPercentage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    if (!merchantAccountId) return;

    setLoader(true);

    /* The API always reports on the payments taken in the trailing 90-day
       window - the widget is not driven by the dashboard's date filter - so
       BREAKDOWN_WINDOW_DAYS here only labels the caption. */
    const subscription$ = DashboardService.getPaymentBreakDown(
      AuthService.authData.accountId,
      merchantAccountId,
    ).subscribe({
      next: (response) => {
        const mappedData = PaymentBreakDownModel.deserializeAPIModel(
          response.result,
        );

        setOneTime(mappedData.oneTimeCount || 0);
        setRecurring(mappedData.recurringCount || 0);
        setTotalCount(mappedData.totalCount || 0);
        setOneTimePercentage(mappedData.oneTimePercentage || 0);
        setRecurringPercentage(mappedData.recurringPercentage || 0);

        setLoader(false);
      },
      error: () => {
        setLoader(false);
        ToastService.showError("Error Occured");
      },
    });

    return () => {
      subscription$.unsubscribe();
    };
  }, [merchantAccountId]);

  if (showLoader) {
    return <>{new Loader(true, "", "330px").loaderComponent}</>;
  }

  const data = [
    {
      label: "One Time",
      value: oneTime,
      percentage: oneTimePercentage,
      color: ONE_TIME_COLOR,
    },
    {
      label: "Recurring",
      value: recurring,
      percentage: recurringPercentage,
      color: RECURRING_COLOR,
    },
  ];

  // Keep the donut visible with an even split when there is no data.
  const chartData =
    totalCount === 0 ? data.map((entry) => ({ ...entry, value: 1 })) : data;

  return (
    <>
      <FlexContainer
        justifyContent="space-between"
        className="activity-header-wrapper"
      >
        <div>
          <FlexContainer
            alignItems="center"
            spacing={10}
            padding="0px 0px 30px"
          >
            <img src={PieChartIcon} alt="payment breakdown" />
            <div className="activity-card-header">Payment Breakdown</div>
          </FlexContainer>
        </div>
      </FlexContainer>

      <FlexContainer justifyContent="center">
        <PieChart width={288} height={170}>
          <Pie
            data={chartData}
            cx="50%"
            cy="50%"
            labelLine={false}
            innerRadius={50}
            outerRadius={80}
            paddingAngle={totalCount === 0 ? 0 : 2}
            dataKey="value"
          >
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
          </Pie>
        </PieChart>
      </FlexContainer>

      {data.map((entry, index) => (
        <FlexContainer
          key={index}
          justifyContent="space-between"
          alignItems="center"
          padding="20px 0px 0px"
        >
          <div>
            <FlexContainer spacing={10} alignItems="center">
              <ColorBox
                heightWidth="20px"
                borderRadius="4px"
                bgColor={entry.color}
              />

              <CustomLabel className="breakdown-label">
                {entry.label}
              </CustomLabel>
            </FlexContainer>
          </div>

          <CustomLabel className="overflow-ellipsis">
            {entry.percentage}%
          </CustomLabel>
        </FlexContainer>
      ))}

      <CustomLabel className="breakdown-caption">
        Transactions last {BREAKDOWN_WINDOW_DAYS} days
      </CustomLabel>
    </>
  );
};

export default observer(PaymentBreakDownWidget);
